import React, { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmModal";
import SortHeader, { useSort } from "@/components/SortHeader";

export default function UsersAdmin() {
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const create = async (form) => {
    try {
      const { data } = await api.post("/admin/users", form);
      setUsers([...users, data.user]);
      setShowForm(false);
      toast.success("Administrateur créé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    const ok = await confirm.ask({
      title: "Supprimer cet administrateur?",
      message: `Toutes les données associées à ${u.email} (événements, officiels, grades, réglages) seront effacées. Irréversible.`,
      confirmLabel: "Supprimer",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers(users.filter((x) => x.id !== u.id));
      toast.success("Administrateur supprimé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const { sortKey, sortDir, cycleSort } = useSort(null);
  const sortedUsers = [...users].sort((a, b) => {
    if (!sortKey) return 0;
    const get = (u) => {
      switch (sortKey) {
        case "email": return (u.email || "").toLowerCase();
        case "name": return (u.name || "").toLowerCase();
        case "business": return (u.business_name || "").toLowerCase();
        case "role": return (u.role || "").toLowerCase();
        default: return "";
      }
    };
    const cmp = get(a).localeCompare(get(b), "fr", { sensitivity: "base" });
    return sortDir === "asc" ? cmp : -cmp;
  });

  return (
    <div data-testid="users-admin-page">
      {confirm.modal}
      {showForm && <CreateAdminModal onSave={create} onClose={() => setShowForm(false)} />}

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="section-number">05 / Utilisateurs</div>
          <h1 className="h1-big">Tes gestionnaires.</h1>
          <p className="text-sm text-[var(--muted)] mt-3 max-w-xl leading-relaxed">
            Crée des comptes administrateurs pour d&apos;autres gestionnaires. Chaque compte a son propre espace de données.
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="new-user-btn">
          <Plus size={14} strokeWidth={2.5} /> Nouvel administrateur
        </button>
      </div>

      {loading ? (
        <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] alert-pulse">Chargement…</p>
      ) : (
        <div className="border-t-2 border-b-2 border-black bg-white">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-black">
                <SortHeader k="email" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} className="!py-3 !px-3">Email</SortHeader>
                <SortHeader k="name" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} className="!py-3 !px-3">Nom</SortHeader>
                <SortHeader k="business" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} className="!py-3 !px-3">Entreprise</SortHeader>
                <SortHeader k="role" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} className="!py-3 !px-3">Rôle</SortHeader>
                <th className="py-3 px-3" />
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => (
                <tr key={u.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-3)]" data-testid={`user-row-${u.id}`}>
                  <td className="py-2.5 px-3 font-bold">{u.email}</td>
                  <td className="py-2.5 px-3">{u.name || "—"}</td>
                  <td className="py-2.5 px-3">{u.business_name || "—"}</td>
                  <td className="py-2.5 px-3">
                    {u.role === "superadmin" ? <span className="badge-dark">Super</span> : <span className="badge">Admin</span>}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    {u.role !== "superadmin" && (
                      <button onClick={() => remove(u)} className="text-[var(--muted)] hover:text-black" data-testid={`delete-user-${u.id}`}>
                        <Trash2 size={13} strokeWidth={2.5} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateAdminModal({ onSave, onClose }) {
  const [f, setF] = useState({ email: "", password: "", name: "", business_name: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.email || !f.password) return toast.error("Email et mot de passe requis");
    if (f.password.length < 8) return toast.error("Mot de passe min. 8 caractères");
    setSaving(true);
    await onSave(f);
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] rounded-2xl p-6 max-w-md w-full shadow-2xl" data-testid="create-admin-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="label">Nouvel administrateur</div>
            <h3 className="font-serif text-3xl">Créer un compte</h3>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-black"><X size={20} /></button>
        </div>
        <div className="space-y-3 mb-5">
          <Field label="Courriel *">
            <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-box" data-testid="new-admin-email-input" />
          </Field>
          <Field label="Mot de passe (min. 8) *">
            <input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="input-box" data-testid="new-admin-password-input" />
          </Field>
          <Field label="Nom">
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input-box" />
          </Field>
          <Field label="Entreprise (apparaît sur les PDF)">
            <input value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} className="input-box" />
          </Field>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Annuler</button>
          <button onClick={submit} disabled={saving} className="btn-primary" data-testid="create-admin-submit-btn">
            {saving ? "Création…" : "Créer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
